import { useCallback, useMemo } from 'react'
import { collection, doc, orderBy, query, serverTimestamp, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { fbt } from 'fbtee'
import { db } from '../lib/firebase'
import { commitUpdates } from '../lib/firestore'
import { runMutation } from '../lib/firestoreMutation'
import { useFirestoreCollection } from './useFirestore'
import { useAuth } from './useAuth'
import { useAdminPendingDocs } from './useAdminPendingDocs'
import { isSystemLead, type ScheduleLead, type ScheduleLeadStatus, type SetTimeWatch } from '../types'

const EMPTY: ScheduleLead[] = []

// Every schedule lead, newest first. Admin-only: rules let a user read their own
// leads, but this subscribes to the whole collection, so non-admins run no query
// at all rather than hitting a permission error.
export function useAdminScheduleLeads() {
  const { isAdmin, user } = useAuth()

  const leadsQuery = useMemo(
    () =>
      isAdmin
        ? (query(collection(db, 'scheduleLeads'), orderBy('createdAt', 'desc')) as Query<ScheduleLead>)
        : null,
    [isAdmin],
  )

  const { data, loading, error } = useFirestoreCollection<ScheduleLead>(leadsQuery)

  // Triage is per event, not per submission: ten people reporting the same
  // festival is one seeding run, so the queue resolves a whole row at once.
  const resolveLeads = useCallback(
    async (ids: string[], status: ScheduleLeadStatus) => {
      const uid = user?.uid
      if (!uid || ids.length === 0) return
      await runMutation(
        fbt('Could not update the lead', 'Error toast when an admin fails to triage a schedule lead'),
        () =>
          commitUpdates(
            db,
            ids.map(id => ({
              ref: doc(db, 'scheduleLeads', id),
              data: { status, processedAt: serverTimestamp(), processedBy: uid },
            })),
          ),
      )
    },
    [user?.uid],
  )

  return { leads: isAdmin ? data : EMPTY, loading, error, resolveLeads }
}

const NO_WATCHES: SetTimeWatch[] = []

// Festivals whose set times are out but that the pipeline couldn't extract on
// its own (setTimeWatch status 'needs-human') — the work a person has to pick up.
export function useAdminSetTimeWatchNeedsHuman() {
  const { isAdmin } = useAuth()
  const needsHumanQuery = useMemo(
    () =>
      isAdmin
        ? (query(collection(db, 'setTimeWatch'), where('status', '==', 'needs-human')) as Query<SetTimeWatch>)
        : null,
    [isAdmin],
  )
  const { data, loading } = useFirestoreCollection<SetTimeWatch>(needsHumanQuery)
  return { watches: isAdmin ? data : NO_WATCHES, loading }
}

// System leads are drained by the schedule-leads routine within minutes, so only
// user reports and needs-human festivals count as work waiting on an admin.
export function useAdminPendingScheduleLeadCount(): number {
  const userLeads = useAdminPendingDocs<ScheduleLead>('scheduleLeads').filter(l => !isSystemLead(l)).length
  return userLeads + useAdminSetTimeWatchNeedsHuman().watches.length
}
